import { Button, Divider } from '@kontentino/ui';
import Modal from 'components/shared/Modal';
import React, { FC, useEffect, useState } from 'react';
import { PageData } from 'types/Page';
import { useMutation, useQuery } from 'react-query';
import { queryKey } from 'constants/queryKey';
import ProfilesSelector from 'app/components/ProfilesSelector';
import { useToast } from 'app/hooks/useToast';
import useSubscriptionInfo from 'app/hooks/useSubscriptionInfo';
import { PostDetail } from 'types/PostDetail';
import Skeleton from 'react-loading-skeleton';
import { useTranslation } from 'react-i18next';
import PostApi from 'api/post';
import PageApi from 'api/page';
import { useInvalidatePostDetailQueries } from 'app/modules/posts/hooks/useInvalidatePostsQueries';
import TranslationLanguageDropdown, {
  TranslationSettings,
} from 'app/modules/posts/modules/translation/TranslationLanguageDropdown';
import { useUserPermissions } from 'app/hooks/useUserPermissions';
import { UserPermission } from 'constants/userPermission';

type Props = {
  post: PostDetail;
  isOpen: boolean;
  onClose: () => void;
};

const DEFAULT_TRANSLATION_SETTINGS: TranslationSettings = {
  translate: null,
};

const PostOverviewLocalPostsSelectProfilesModal: FC<Props> = ({
  post,
  isOpen,
  onClose,
}) => {
  const [selectedProfilesIds, setSelectedProfilesIds] = useState<number[]>([]);
  const [translationSettings, setTranslationSettings] = useState<
    Record<number, TranslationSettings>
  >({});
  const [brandProfilesByPage, setBrandProfilesByPage] = useState<
    Record<number, number | null>
  >({});
  const toast = useToast();
  const { t } = useTranslation();
  const { accountPlanType } = useSubscriptionInfo();
  const { hasSome } = useUserPermissions();
  const canTranslate = hasSome(UserPermission.POST_EDIT_TEXT);
  const { data: localPages, isLoading: localPagesLoading } = useQuery(
    queryKey.localPages(post.page.id),
    () => PageApi.localPages(post.page.id),
  );
  const invalidatePostDetail = useInvalidatePostDetailQueries();

  const distributeLocalPages = useMutation(
    PostApi.distributeGlobalPostToLocalPages,
    {
      onSuccess: () => {
        onClose();
        invalidatePostDetail({
          postId: post.id,
        });
        toast(
          t('globalPages.distributedPostAcrossAllSelectedLocalProfiles'),
          'success',
        );
      },
      onError: () => {
        toast(t('somethingWentWrong'), 'error');
      },
    },
  );

  useEffect(() => {
    if (!isOpen) {
      setSelectedProfilesIds([]);
      setTranslationSettings({});
    }
  }, [isOpen]);

  const handleTranslationChange = (
    pageId: number,
    settings: TranslationSettings,
  ) => {
    setTranslationSettings((prev) => ({
      ...prev,
      [pageId]: settings,
    }));
  };

  const getTranslationSettingsForPage = (pageId: number): TranslationSettings =>
    translationSettings[pageId] || DEFAULT_TRANSLATION_SETTINGS;

  const onSelectProfile = (profileId: number) => {
    setSelectedProfilesIds((prevSelected) => {
      const isAlreadySelected = prevSelected.includes(profileId);

      if (isAlreadySelected) {
        return prevSelected.filter((id) => id !== profileId);
      } else {
        return [...prevSelected, profileId];
      }
    });
  };

  const localPostPageIds =
    post.localPosts?.data.map((post) => post.page.id) ?? [];

  const profiles = (localPages?.data ?? []).filter(
    (profile) => !localPostPageIds.includes(profile.id),
  );

  return (
    <Modal
      size="sm"
      open={isOpen}
      onClose={onClose}
      dataCy={{
        wrapper: 'local-posts-select-profile-modal',
        closeButton: 'local-posts-select-profile-modal_close-button',
      }}
    >
      <Modal.Header title={t('globalPages.selectLocaLProfiles')} />
      <Modal.Content>
        {!localPagesLoading && profiles.length > 0 && (
          <ProfilesSelector
            profiles={profiles as PageData[]}
            onSelect={(id: number) => onSelectProfile(id)}
            selectedIds={selectedProfilesIds}
            onToggleAllSelection={(selection) =>
              selection === 'all'
                ? setSelectedProfilesIds(profiles.map(({ id }) => id))
                : setSelectedProfilesIds([])
            }
            renderRowEnd={(profile, isSelected) =>
              isSelected && canTranslate ? (
                <TranslationLanguageDropdown
                  pageId={profile.id}
                  value={getTranslationSettingsForPage(profile.id)}
                  onChange={(settings) =>
                    handleTranslationChange(profile.id, settings)
                  }
                  selectedBrandProfileId={
                    brandProfilesByPage[profile.id] ?? null
                  }
                  onBrandProfileChange={(brandProfileId) =>
                    setBrandProfilesByPage((prev) => ({
                      ...prev,
                      [profile.id]: brandProfileId,
                    }))
                  }
                />
              ) : null
            }
          />
        )}

        {!localPagesLoading && profiles.length === 0 && (
          <div className="tw-flex tw-items-center tw-justify-center tw-py-5">
            <span className="tw-font-regular tw-text-grayscale-100">
              {t('globalPages.noLocalProfilesAvailable')}
            </span>
          </div>
        )}

        {localPagesLoading && profiles.length === 0 && (
          <div className="tw-mb-2 tw-h-full tw-w-full">
            <Skeleton width="100%" height={250} />
          </div>
        )}
      </Modal.Content>
      <Divider />
      <Modal.Footer>
        {profiles.length ? (
          <Button
            isLoading={distributeLocalPages.isLoading}
            disabled={!selectedProfilesIds.length}
            onClick={() => {
              const translations = Object.entries(translationSettings).map(
                ([pageId, settings]) => ({
                  pageId: Number(pageId),
                  ...settings,
                }),
              );

              distributeLocalPages.mutate({
                postId: post.id,
                pageIds: selectedProfilesIds,
                translations,
                planType: accountPlanType,
              });
            }}
            className="tw-w-full"
          >
            {t('confirm')}
          </Button>
        ) : (
          <Button
            isLoading={distributeLocalPages.isLoading}
            onClick={onClose}
            className="tw-w-full"
          >
            {t('close')}
          </Button>
        )}
      </Modal.Footer>
    </Modal>
  );
};
export default PostOverviewLocalPostsSelectProfilesModal;
